/**
 * CAS Registry Number normalization and validation, mirroring the backend's
 * `normalize_cas` / `is_valid_cas` (`backend/turnus_ai/mc_tools/validators.py`)
 * EXACTLY — trim-only, no case-folding, format + check-digit validated.
 *
 * DO NOT CONFUSE WITH `normalizeCas` in
 * `../components/declarations/review/studio-v2/mapping/conditionRowMerge.ts`.
 * That function case-folds and collapses whitespace for matching extracted
 * text against a registry entry's identity (grouping semantics) and never
 * validates format or checksum. This module has a different contract
 * entirely: it's for validating/normalizing a CAS number a user types into
 * the "add substance" form before it's submitted to the backend, matching
 * the backend's own `normalize_cas` (trim only — a real CAS number has no
 * letters, so there's nothing to case-fold) and `is_valid_cas` (format +
 * checksum). The two are named differently on purpose so they can never be
 * mixed up: use `normalizeCasNumber` / `isValidCasNumber` here for
 * form validation, never `normalizeCas` from `conditionRowMerge.ts`.
 */

/**
 * Trims whitespace from a CAS number string. Returns `null` for falsy input
 * (`null`, `undefined`, empty string, whitespace-only string) — mirrors the
 * backend's `normalize_cas`. Does NOT validate format and does NOT alter
 * casing or any other characters of a valid value.
 */
export function normalizeCasNumber(
  value: string | null | undefined,
): string | null {
  if (!value) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** `^\d{2,7}-\d{2}-\d$` — the CAS Registry Number format (excluding whitespace, which callers should normalize first). */
const CAS_FORMAT = /^\d{2,7}-\d{2}-\d$/

/**
 * Why a CAS Registry Number was rejected, or `null` when it is valid.
 *
 * The two failure modes need different copy: `"format"` means the value is
 * not shaped like a CAS number at all, so telling the user the expected
 * shape helps. `"check_digit"` means it IS shaped correctly but the final
 * digit does not verify — telling that user about the format would be
 * actively misleading, since the format is exactly what they got right.
 */
export type CasNumberError = "format" | "check_digit"

/**
 * Validates a CAS Registry Number's format AND check digit, mirroring the
 * backend's `is_valid_cas` exactly, and reports WHICH of the two failed:
 *
 * 1. Format must match `^\d{2,7}-\d{2}-\d$`, else `"format"`.
 * 2. Strip both hyphens and the final digit (the check digit); reverse the
 *    remaining digit string; compute a 1-indexed position-weighted sum of
 *    those digits; the sum mod 10 must equal the check digit, else
 *    `"check_digit"`.
 *
 * Worked example: `7732-18-5` (water) — digits excluding the check digit are
 * `773218`, reversed `812377`, weighted sum
 * `8*1 + 1*2 + 2*3 + 3*4 + 7*5 + 7*6 = 105`, `105 % 10 = 5` — matches the
 * check digit, so it's valid.
 *
 * Returns `"format"` for `null`/`undefined`/empty/whitespace-only input, and
 * for the literal placeholder values seeded in the real catalog (`"-"`,
 * `"Various"`) — neither matches the format regex, so they fall out
 * naturally without special-casing.
 */
export function casNumberError(
  value: string | null | undefined,
): CasNumberError | null {
  const normalized = normalizeCasNumber(value)
  if (normalized === null) return "format"
  if (!CAS_FORMAT.test(normalized)) return "format"

  const digits = normalized.replace(/-/g, "")
  const checkDigit = Number(digits[digits.length - 1])
  const body = digits.slice(0, -1).split("").reverse()

  const sum = body.reduce(
    (acc, digit, index) => acc + Number(digit) * (index + 1),
    0,
  )

  return sum % 10 === checkDigit ? null : "check_digit"
}

/**
 * Whether a CAS Registry Number is valid at all. Defined in terms of
 * `casNumberError` so the check-digit arithmetic lives in exactly one place;
 * callers that need to explain the rejection should use that instead.
 */
export function isValidCasNumber(value: string | null | undefined): boolean {
  return casNumberError(value) === null
}
